import RouteTransition from '@/components/routeTransition/RouteTransition'
import { Button } from '@/components/ui/button'
import LoadingIndicator from '@/components/upseat-ui/seatMap/LoadingIndicator'
import { useUserStore } from '@/context/useUserStore'
import useCallRouteWithDirection from '@/hooks/ui/useCallRouteWithDirection'
import PublicLayout from '@/layouts/PublicLayout'
import { transition } from '@/libs/utils/constants'
import { postTokenVerify } from '@/services/Login/postTokenVerify'
import { postRedeemTicket } from '@/services/Share/postRedeemTicket'
import { tokenStore } from '@/services/tokenStore'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

type ClaimState = 'loading' | 'success' | 'error'

const ERROR_MESSAGES: Record<number, string> = {
  400: 'This ticket has already been claimed or the link has expired.',
  404: 'This share link is invalid.',
}

const ClaimTicketPage = () => {
  const { uid } = useParams<{ uid: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const callRouteWithDirection = useCallRouteWithDirection()
  const { setUser } = useUserStore()

  const [claimState, setClaimState] = useState<ClaimState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (!uid) {
      setErrorMessage(
        'Invalid share link. Please ask the sender for a new one.',
      )
      setClaimState('error')
      return
    }

    const run = token
      ? (() => {
          tokenStore.clear()
          localStorage.removeItem('user')
          return postTokenVerify(token).then((res) => {
            setUser({
              uid: res.user.uid,
              username: res.user.email.split('@')[0],
              firstname: res.user.first_name,
              lastname: res.user.last_name,
              email: res.user.email,
              phoneNumber: res.user.phone_number,
              cookies: res.user.accepted_cookies ?? undefined,
              marketing: res.user.accepted_marketing ?? undefined,
              preferences_required: res.cookie_prompt_required,
            })
            if (res.details_required) {
              const params = new URLSearchParams({ redirect: `/share/${uid}` })
              if (res.user.first_name)
                params.set('firstName', res.user.first_name)
              if (res.user.last_name) params.set('lastName', res.user.last_name)
              callRouteWithDirection(
                `/extra-details?${params.toString()}`,
                true,
                1,
              )
              return
            }
            return postRedeemTicket(uid)
          })
        })()
      : postRedeemTicket(uid)

    run
      .then((result) => {
        if (result !== undefined) setClaimState('success')
      })
      .catch((err) => {
        const status = err?.response?.status
        setErrorMessage(
          ERROR_MESSAGES[status] ?? 'Something went wrong. Please try again.',
        )
        setClaimState('error')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGoToTickets = () => {
    callRouteWithDirection('/tickets', true, 1)
  }

  if (claimState === 'loading') {
    return (
      <div className="w-full h-[100dvh] bg-purple flex flex-col items-center justify-center gap-4">
        <LoadingIndicator />
        <p className="text-body text-white">{`Claiming your ticket…`}</p>
      </div>
    )
  }

  if (claimState === 'success') {
    return (
      <PublicLayout>
        <RouteTransition>
          <main className="min-h-[100svh] flex flex-col justify-end px-4 py-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={transition}
              className="flex flex-col gap-2 text-white"
            >
              <h3 className="text-h3">{`ticket claimed! 🎟️`}</h3>
              <p className="text-sm text-purple5 text-pretty">
                {`Your ticket has been added to your account.`}
              </p>
            </motion.div>
            <Button
              variant="primaryYellow"
              size="large"
              className="w-full"
              onClick={handleGoToTickets}
            >
              {`View my tickets`}
            </Button>
          </main>
        </RouteTransition>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <RouteTransition>
        <main className="min-h-[100svh] flex flex-col justify-end px-4 py-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={transition}
            className="flex flex-col gap-2 text-white"
          >
            <h3 className="text-h3">{`couldn't claim ticket`}</h3>
            <p className="text-sm text-purple5 text-pretty">{errorMessage}</p>
          </motion.div>
          <Button
            variant="primaryYellow"
            size="large"
            className="w-full"
            onClick={handleGoToTickets}
          >
            {`Go to my tickets`}
          </Button>
        </main>
      </RouteTransition>
    </PublicLayout>
  )
}

export default ClaimTicketPage
